import React, { useCallback, useEffect, useState } from "react";
import { useImportApi } from "./importApi";
import { fmtDateTime, IrisLeadLink, toastApiError, useMountedRef } from "./common";
import "./Import.css";

// Admin-only activity log (GET /import/audit). Every change the importer made
// to Iris — from the web app or the leadscrub MCP agent — with who, why
// (ticket / note) and the exact values. Read-only: the log is evidence.

const ACTION_LABELS = {
	file_uploaded: "File uploaded",
	contacts_previewed: "Preview (no changes)",
	batch_queued: "Batch queued",
	batch_started: "Batch started",
	batch_finished: "Batch finished",
	batch_aborted: "Batch ABORTED",
	batch_resumed: "Batch resumed",
	lead_created: "Lead created",
	lead_updated: "Contacts added",
	lead_skipped: "Lead skipped",
	lead_failed: "Lead failed",
	verify_failed: "VERIFY FAILED",
	write_unconfirmed: "Write unconfirmed",
	revert_scanned: "Undo check (no changes)",
	revert_started: "Undo started",
	lead_deleted: "Lead deleted (undo)",
	contacts_deleted: "Contacts removed (undo)",
	revert_conflict: "Undo conflict",
	revert_skipped: "Undo skipped",
	revert_lead_gone: "Lead already gone",
	revert_error: "Undo error",
	revert_finished: "Undo finished",
};

// Actions that mean "something needs a human look".
const ALERT_ACTIONS = new Set([
	"batch_aborted",
	"verify_failed",
	"write_unconfirmed",
	"lead_failed",
	"revert_conflict",
	"revert_error",
]);

const EMPTY_FILTERS = { lead_id: "", batch_id: "", ticket: "", via: "", action: "" };

function cleanFilters(filters) {
	const out = {};
	Object.entries(filters).forEach(([k, v]) => {
		if (String(v ?? "").trim() !== "") out[k] = String(v).trim();
	});
	return out;
}

function ViaChip({ via }) {
	const agent = via === "mcp";
	return (
		<span
			className="statusChip"
			title={agent ? "Done by the leadscrub MCP agent" : "Done in the web app"}
			style={{
				backgroundColor: agent ? "#faf5ff" : "#edf2f7",
				color: agent ? "#6b46c1" : "#4a5568",
				border: `1px solid ${agent ? "#d6bcfa" : "#cbd5e0"}`,
			}}>
			{agent ? "agent" : "web"}
		</span>
	);
}

export default function ActivityLog({ active }) {
	const api = useImportApi();
	const mountedRef = useMountedRef();

	const [draft, setDraft] = useState(EMPTY_FILTERS);
	const [filters, setFilters] = useState({});
	const [events, setEvents] = useState([]);
	const [nextBeforeId, setNextBeforeId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [expanded, setExpanded] = useState(null);

	const load = useCallback(
		async (appliedFilters, beforeId = null) => {
			setLoading(true);
			try {
				const data = await api.listAudit({
					...appliedFilters,
					...(beforeId ? { before_id: beforeId } : {}),
				});
				if (!mountedRef.current) return;
				const page = data?.events ?? [];
				setEvents((prev) => (beforeId ? [...prev, ...page] : page));
				setNextBeforeId(data?.next_before_id ?? null);
			} catch (err) {
				toastApiError(err, "Could not load the activity log");
			} finally {
				if (mountedRef.current) setLoading(false);
			}
		},
		[api, mountedRef]
	);

	useEffect(() => {
		if (active) load(filters);
	}, [active, filters, load]);

	const applyFilters = (e) => {
		e.preventDefault();
		setExpanded(null);
		setFilters(cleanFilters(draft));
	};

	const resetFilters = () => {
		setDraft(EMPTY_FILTERS);
		setExpanded(null);
		setFilters({});
	};

	const exportLog = async () => {
		try {
			await api.exportAudit(filters);
		} catch (err) {
			toastApiError(err, "Export failed");
		}
	};

	const setField = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

	return (
		<div className="importCard">
			<div className="batchDetailHeader">
				<h2 style={{ margin: 0 }}>Activity log</h2>
				<div style={{ display: "flex", gap: 8 }}>
					<button
						type="button"
						className="importBtn secondary small"
						onClick={() => load(filters)}
						disabled={loading}>
						{loading ? "Loading…" : "Refresh"}
					</button>
					<button
						type="button"
						className="importBtn small"
						onClick={exportLog}
						title="Download these events as JSON Lines — one event per line, for an agent to review">
						Export for agent (.jsonl)
					</button>
				</div>
			</div>
			<p className="importMuted" style={{ marginTop: 0, fontSize: 13 }}>
				Every change the importer made to Iris, from the web app or the agent.
				Only admins can see this page.
			</p>

			<form className="activityFilters" onSubmit={applyFilters}>
				<input
					className="importInput"
					placeholder="Iris lead id"
					inputMode="numeric"
					value={draft.lead_id}
					onChange={setField("lead_id")}
				/>
				<input
					className="importInput"
					placeholder="Batch #"
					inputMode="numeric"
					value={draft.batch_id}
					onChange={setField("batch_id")}
				/>
				<input
					className="importInput"
					placeholder="Ticket (e.g. ITSM-1362)"
					value={draft.ticket}
					onChange={setField("ticket")}
				/>
				<select className="importSelect" value={draft.via} onChange={setField("via")}>
					<option value="">Web + agent</option>
					<option value="mcp">Agent only</option>
					<option value="web">Web only</option>
				</select>
				<select className="importSelect" value={draft.action} onChange={setField("action")}>
					<option value="">All actions</option>
					{Object.entries(ACTION_LABELS).map(([key, label]) => (
						<option key={key} value={key}>
							{label}
						</option>
					))}
				</select>
				<button type="submit" className="importBtn small">
					Filter
				</button>
				<button type="button" className="importBtn secondary small" onClick={resetFilters}>
					Clear
				</button>
			</form>

			{events.length === 0 ? (
				<p className="importMuted" style={{ fontStyle: "italic" }}>
					{loading ? "Loading…" : "No activity matches these filters."}
				</p>
			) : (
				<div className="importTableWrap" style={{ maxHeight: 560, overflowY: "auto" }}>
					<table className="importTable">
						<thead>
							<tr>
								<th>When</th>
								<th>Who</th>
								<th>Via</th>
								<th>Action</th>
								<th>Lead</th>
								<th>Batch</th>
								<th>Ticket</th>
								<th>What happened</th>
							</tr>
						</thead>
						<tbody>
							{events.map((ev) => {
								const open = expanded === ev.id;
								const alert = ALERT_ACTIONS.has(ev.action);
								return (
									<React.Fragment key={ev.id}>
										<tr
											className={`clickableRow ${open ? "selectedRow" : ""} ${alert ? "activityAlert" : ""}`}
											onClick={() => setExpanded(open ? null : ev.id)}
											title="Click for the full details">
											<td style={{ whiteSpace: "nowrap" }}>{fmtDateTime(ev.created_at)}</td>
											<td>{ev.actor}</td>
											<td>
												<ViaChip via={ev.via} />
											</td>
											<td style={{ whiteSpace: "nowrap" }}>
												{ACTION_LABELS[ev.action] || ev.action}
											</td>
											<td onClick={(e) => e.stopPropagation()}>
												<IrisLeadLink leadId={ev.iris_lead_id} />
											</td>
											<td>{ev.batch_id ? `#${ev.batch_id}` : "—"}</td>
											<td>{ev.ticket || "—"}</td>
											<td>{ev.summary}</td>
										</tr>
										{open && (
											<tr>
												<td colSpan={8}>
													<pre className="activityDetails">
														{JSON.stringify(ev.details ?? {}, null, 2)}
													</pre>
												</td>
											</tr>
										)}
									</React.Fragment>
								);
							})}
						</tbody>
					</table>
				</div>
			)}

			{nextBeforeId && (
				<button
					type="button"
					className="importBtn secondary small"
					style={{ marginTop: 10 }}
					onClick={() => load(filters, nextBeforeId)}
					disabled={loading}>
					Load older
				</button>
			)}
		</div>
	);
}
